<template>
  <div class="tabs-component">
    <div v-if="isDesktop" class="tabs-component__container">
      <button
        v-for="tab in tabsData"
        :key="tab.key"
        class="tabs-component__tab"
        :class="[
          {
            active: activeTab === tab.key,
            [tab.colorClass]: activeTab === tab.key,
          },
        ]"
        @click="activeTab = tab.key"
      >
        {{ tab.title }}
      </button>
    </div>

    <div
      v-if="!isMobile"
      class="tabs-component__grid"
      :style="{ backgroundColor: activeTabColor }"
    >
      <RatingCard
        v-for="(card, index) in activeCards"
        :key="index"
        :card="card"
        :class="['tabs-component__card', `tabs-component__card-${index + 1}`]"
      />
    </div>

    <div v-if="isMobile" class="tabs-component__grid--mobile">
      <div
        v-for="tab in tabsData"
        :key="tab.key"
        class="tabs-component__tab-container"
        :class="[{ active: activeTab === tab.key }]"
      >
        <button
          class="tabs-component__tab"
          :class="[tab.colorClass]"
          @click.prevent="handleToggle(tab.key)"
        >
          {{ tab.title }}
        </button>

        <div
          v-if="activeTab === tab.key"
          :style="{ backgroundColor: activeTabColor }"
          class="tabs-component__content"
        >
          <RatingCard
            v-for="(card, index) in activeCards"
            :key="index"
            :card="card"
            :class="[
              'tabs-component__card',
              `tabs-component__card-${index + 1}`,
            ]"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  computed,
  useSanityQuery,
  ref,
  useDevice,
  useTrackEvent,
  useRoute,
} from '#imports';
import { getQuery as getSpecialQuery } from '~/groq-queries/z-rating';

const contentQuery = getSpecialQuery();
const { data: content } = await useSanityQuery(contentQuery);
const activeTab = ref('commonRatingCards');
const { isMobile, isDesktop } = useDevice();
const currentRoute = useRoute();

function trackEvent(eventType, name) {
  useTrackEvent(eventType, {
    props: {
      name,
      placement: currentRoute.path,
    },
  });
}

const tabsData = [
  {
    key: 'commonRatingCards',
    title: 'Общий рейтинг',
    cards: computed(() => content.value.allCards.commonRatingCards),
    colorClass: 'tab-color-common',
    color: '#4B5788',
  },
  {
    key: 'positionRatingCards',
    title: 'Публичная позиция',
    cards: computed(() => content.value.allCards.positionRatingCards),
    colorClass: 'tab-color-position',
    color: '#82B72A',
  },
  {
    key: 'propagandaRatingCards',
    title: 'Пропаганда',
    cards: computed(() => content.value.allCards.propagandaRatingCards),
    colorClass: 'tab-color-propaganda',
    color: '#F09738',
  },
  {
    key: 'forcingRatingCards',
    title: 'Принуждение',
    cards: computed(() => content.value.allCards.forcingRatingCards),
    colorClass: 'tab-color-forcing',
    color: '#643369',
  },
  {
    key: 'pushingRatingCards',
    title: 'Давление',
    cards: computed(() => content.value.allCards.pushingRatingCards),
    colorClass: 'tab-color-pushing',
    color: '#B4391C',
  },
];

const activeTabColor = computed(() => {
  const active = tabsData.find((tab) => tab.key === activeTab.value);
  return active ? active.color : '#938c92';
});

const activeCards = computed(() => {
  const tab = tabsData.find((t) => t.key === activeTab.value);
  return tab ? tab.cards.value : [];
});

const toggleTab = (tabKey) => {
  if (isMobile) {
    activeTab.value = activeTab.value === tabKey ? undefined : tabKey;
  } else {
    activeTab.value = tabKey;
  }
};

const handleToggle = (tabKey) => {
  toggleTab(tabKey);
  trackEvent('click', `Click on ${tabKey} tab`);
};
</script>

<style lang="postcss">
.tabs-component {
  max-width: 1040px;
  margin: 50px auto;

  @media (--tablet-vp) {
    padding: 0 60px;
  }

  @media (--small-vp) {
    padding: 0;
  }

  &__content {
    padding: 0 16px 84px 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;

    &:last-child {
      padding: 0 16px 24px 16px;
      border-radius: 0 0 16px 16px;
    }
  }

  &__tab {
    cursor: pointer;
    padding: 5px 32px;
    width: 100%;
    border-radius: 16px 16px 0 0;
    font-size: 18px;
    line-height: 22px;
    font-weight: 700;
    color: #fff;
    border-right: 1px solid var(--page-background-color);
    background: #aaa;
    font-family: var(--title-font-family);

    &:last-of-type {
      border-right: 0;
    }

    @media (--tablet-vp) {
      font-size: 24px;
      line-height: 28px;
      padding: 16px 16px 52px;

      &.active {
        padding: 16px 16px 24px;
      }
    }
  }

  &__tab-container {
    margin-top: -40px;

    &:last-of-type {
      padding-bottom: 16px;
      border-radius: 16px;

      .tabs-component__tab {
        padding: 16px;
        border-radius: 16px;
      }
    }

    &.active {
      .tabs-component__tab {
        padding: 16px;
        border-radius: 16px 16px 0 0;
      }

      .tabs-component__content {
        padding: 0 16px 74px 16px;
      }
    }

    &.active:last-of-type {
      .tabs-component__content {
        padding: 0 16px 44px 16px;
      }
    }
  }

  &__container {
    display: flex;
    justify-content: space-between;
    margin-bottom: -15px;

    @media (--tablet-vp) {
      display: none;
    }
  }

  & .tab-color-common {
    background: #4b5788;
  }

  & .tab-color-position {
    background: #82b72a;
  }

  & .tab-color-propaganda {
    background: #f09738;
  }

  & .tab-color-forcing {
    background: #643369;
  }

  & .tab-color-pushing {
    background: #b4391c;
  }

  &__grid {
    padding: 40px 24px;
    border-radius: 16px;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr 1fr;
    grid-template-rows: 64px 64px 64px 64px 64px 64px 64px 64px;
    gap: 24px 24px;
    grid-template-areas:
      'card-1 card-1 card-1 card-2 card-2 card-2'
      'card-1 card-1 card-1 card-2 card-2 card-2'
      'card-1 card-1 card-1 card-2 card-2 card-2'
      'card-1 card-1 card-1 card-2 card-2 card-2'
      'card-1 card-1 card-1 card-3 card-3 card-4'
      'card-1 card-1 card-1 card-3 card-3 card-4'
      'card-1 card-1 card-1 card-3 card-3 card-5'
      'card-1 card-1 card-1 card-3 card-3 card-5';

    @media (--tablet-vp) {
      grid-template-columns: 1fr;
      grid-template-rows: repeat(8, auto);
      grid-template-areas:
        'card-1'
        'card-1'
        'card-1'
        'card-1'
        'card-2'
        'card-2'
        'card-2'
        'card-2'
        'card-3'
        'card-3'
        'card-4'
        'card-4'
        'card-5'
        'card-5';
    }
  }

  &__grid--mobile {
    margin: 80px 0;
  }

  &__card {
    background-color: #e0e0e0;
    padding: 1rem;
    text-align: center;

    .rating-card__name {
      text-align: left;
    }

    @media (--tablet-vp) {
      height: 130px;
    }
  }

  &__card-1 {
    grid-area: card-1;

    & .rating-card__image::before {
      max-width: 400px;
    }

    @media (--tablet-vp) {
      height: 150px;
    }
  }

  &__card-2 {
    grid-area: card-2;
    @media (--tablet-vp) {
      height: 130px;
    }
  }

  &__card-3 {
    grid-area: card-3;
    @media (--tablet-vp) {
      height: 110px;
    }
  }

  &__card-4 {
    grid-area: card-4;
    min-width: 205px;

    .rating-card__image {
      display: none;
    }

    h2.rating-card__name {
      font-size: 24px;
      line-height: 28px;
    }

    h3.rating-card__score {
      font-size: 20px;
      line-height: 24px;
    }

    .rating-card__score span {
      display: none;
    }

    @media (--tablet-vp) {
      height: 90px;

      h2.rating-card__name {
        font-size: 16px;
        line-height: 20px;
      }

      h3.rating-card__score {
        font-size: 16px;
        line-height: 20px;
      }

      .rating-card__score span {
        display: inline-block;
      }
    }
  }

  &__card-5 {
    grid-area: card-5;
    min-width: 205px;

    .rating-card__image {
      display: none;
    }

    h2.rating-card__name {
      font-size: 24px;
      line-height: 28px;
    }

    h3.rating-card__score {
      font-size: 20px;
      line-height: 24px;
    }

    .rating-card__score span {
      display: none;
    }

    @media (--tablet-vp) {
      height: 90px;

      h2.rating-card__name {
        font-size: 16px;
        line-height: 20px;
      }

      h3.rating-card__score {
        font-size: 16px;
        line-height: 20px;
      }

      .rating-card__score span {
        display: inline-block;
      }
    }
  }
}
</style>
